import { talkBrand } from "./brand";

const STORAGE_KEY = `${talkBrand.storagePrefix}:talk-room-appearance:v1`;
const MAX_ROOM_OVERRIDES = 50;

interface RoomAppearanceOverride {
  haramMode: boolean;
  updatedAt: number;
}

type StoredRoomAppearance = Record<string, RoomAppearanceOverride>;

function validRoomId(roomId: string): boolean {
  return roomId.length > 0 && roomId.length <= 128;
}

function readOverrides(): StoredRoomAppearance {
  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? "{}");
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    return Object.fromEntries(Object.entries(parsed).filter(([roomId, value]) => {
      if (!validRoomId(roomId) || !value || typeof value !== "object") return false;
      const candidate = value as Partial<RoomAppearanceOverride>;
      return typeof candidate.haramMode === "boolean" && typeof candidate.updatedAt === "number" && Number.isFinite(candidate.updatedAt);
    })) as StoredRoomAppearance;
  } catch {
    return {};
  }
}

export function loadRoomHaramMode(roomId: string, roomDefault = false): boolean {
  if (!talkBrand.supportsHaramMode) return false;
  if (!validRoomId(roomId)) return roomDefault;
  return readOverrides()[roomId]?.haramMode ?? roomDefault;
}

export function saveRoomHaramMode(roomId: string, enabled: boolean, now = Date.now()): void {
  if (!talkBrand.supportsHaramMode) return;
  if (!validRoomId(roomId)) return;
  try {
    const entries = Object.entries({ ...readOverrides(), [roomId]: { haramMode: enabled, updatedAt: now } })
      .sort(([, left], [, right]) => right.updatedAt - left.updatedAt)
      .slice(0, MAX_ROOM_OVERRIDES);
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(entries)));
  } catch {
    // Hardened browser modes can disable storage; the in-memory room setting still works.
  }
}
